<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import Icon from './Icon.svelte';
  import { onMount } from 'svelte';
  import {
    fetchPublicDataTables,
    fetchDedupRules,
    createDedupRule,
    updateDedupRule,
    deleteDedupRule,
    type ApiTable,
    type ApiField,
    type DedupRule,
  } from '../lib/data-tables-api';
  import { fieldType } from '../lib/data-tables-catalog';
  import { tableIconName } from '../lib/icons';

  let tables = $state<ApiTable[]>([]);
  let rules = $state<DedupRule[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  /** slug → collapsed?: boolean. За замовчуванням розкрито (false). */
  let collapsed = $state<Record<string, boolean>>({});

  // Editor modal state.
  type EditorMode =
    | { kind: 'create'; tableSlug: string }
    | { kind: 'edit'; rule: DedupRule };
  let editor = $state<EditorMode | null>(null);
  let editorFieldKeys = $state<string[]>([]);
  let editorBehavior = $state<'warn' | 'block'>('warn');
  let editorBusy = $state(false);
  let editorErr = $state<string | null>(null);
  /** Який саме рядок field-picker зараз відкритий (індекс) — або null. */
  let pickerOpenIdx = $state<number | null>(null);
  let pickerSearch = $state('');

  async function load() {
    loading = true;
    err = null;
    try {
      const [t, r] = await Promise.all([fetchPublicDataTables(), fetchDedupRules()]);
      tables = t.items;
      rules = r.items;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  function rulesFor(slug: string): DedupRule[] {
    return rules.filter((r) => r.tableSlug === slug);
  }
  function fieldsFor(slug: string): ApiField[] {
    return tables.find((t) => t.slug === slug)?.fields ?? [];
  }
  function fieldLabel(slug: string, fieldKey: string): string {
    const f = fieldsFor(slug).find((x) => x.fieldKey === fieldKey);
    return f?.label ?? fieldKey;
  }
  function fieldTypeKey(slug: string, fieldKey: string) {
    const f = fieldsFor(slug).find((x) => x.fieldKey === fieldKey);
    return f ? fieldType(f.type).key : 'single_line';
  }

  function toggleCollapsed(slug: string) {
    collapsed = { ...collapsed, [slug]: !collapsed[slug] };
  }

  function openCreate(slug: string) {
    editor = { kind: 'create', tableSlug: slug };
    editorFieldKeys = [];
    editorBehavior = 'warn';
    editorErr = null;
    pickerOpenIdx = null;
    pickerSearch = '';
  }
  function openEdit(r: DedupRule) {
    editor = { kind: 'edit', rule: r };
    editorFieldKeys = [...r.fieldKeys];
    editorBehavior = r.onCreateBehavior;
    editorErr = null;
    pickerOpenIdx = null;
    pickerSearch = '';
  }
  function closeEditor() {
    editor = null;
  }

  function currentSlug(): string {
    if (!editor) return '';
    return editor.kind === 'create' ? editor.tableSlug : editor.rule.tableSlug;
  }
  const isBuiltInRule = $derived(editor?.kind === 'edit' && editor.rule.builtIn);

  function addFieldSlot() {
    editorFieldKeys = [...editorFieldKeys, ''];
    pickerOpenIdx = editorFieldKeys.length - 1;
    pickerSearch = '';
  }
  function removeFieldSlot(idx: number) {
    editorFieldKeys = editorFieldKeys.filter((_, i) => i !== idx);
    pickerOpenIdx = null;
  }
  function pickFieldFor(idx: number, fk: string) {
    const next = [...editorFieldKeys];
    next[idx] = fk;
    editorFieldKeys = next;
    pickerOpenIdx = null;
    pickerSearch = '';
  }

  async function saveEditor() {
    if (!editor || isBuiltInRule) return;
    const fks = editorFieldKeys.filter((k) => k && k.trim() !== '');
    if (fks.length === 0) {
      editorErr = 'Оберіть хоча б одне поле';
      return;
    }
    editorBusy = true;
    editorErr = null;
    try {
      if (editor.kind === 'create') {
        await createDedupRule({
          tableSlug: editor.tableSlug,
          fieldKeys: fks,
          onCreateBehavior: editorBehavior,
        });
      } else {
        await updateDedupRule(editor.rule.id, {
          fieldKeys: fks,
          onCreateBehavior: editorBehavior,
        });
      }
      closeEditor();
      await load();
    } catch (e) {
      editorErr = e instanceof Error ? e.message : String(e);
    } finally {
      editorBusy = false;
    }
  }

  async function onDeleteRule(r: DedupRule) {
    if (r.builtIn) return;
    if (!confirm('Видалити це правило?')) return;
    try {
      await deleteDedupRule(r.id);
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    }
  }

  function filteredFields(slug: string, query: string, exclude: string[]): ApiField[] {
    const q = query.trim().toLowerCase();
    return fieldsFor(slug).filter((f) => {
      if (exclude.includes(f.fieldKey)) return false;
      if (!q) return true;
      return f.label.toLowerCase().includes(q) || f.fieldKey.toLowerCase().includes(q);
    });
  }
</script>

<header class="mb-5">
  <h1 class="text-xl font-semibold leading-tight">Визначення дублікатів</h1>
  <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
    Правила, за якими записи у таблицях вважаються дублікатами одного й того ж обʼєкта.
    Built-in правила дзеркалять існуючі DB-constraints і не видаляються.
  </p>
</header>

{#if loading}
  <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else if err}
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
{:else}
  <div class="space-y-3">
    {#each tables as t (t.slug)}
      {@const tableRules = rulesFor(t.slug)}
      {@const isCollapsed = !!collapsed[t.slug]}
      <section class="overflow-hidden rounded-xl border border-[var(--color-border-3)] bg-white">
        <!-- Card header -->
        <button
          type="button"
          onclick={() => toggleCollapsed(t.slug)}
          class="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-[var(--color-bg-3)]"
        >
          <div
            class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white"
            style="background: {t.color}"
          ><Icon name={tableIconName(t.icon)} size={16} /></div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold">{t.name}</span>
              {#if t.builtIn}
                <span class="rounded-full bg-[var(--color-pu-light)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-pu-3)]">
                  Built-in
                </span>
              {/if}
            </div>
            <div class="mt-0.5 text-[11.5px] text-[var(--color-text-muted)]">
              {tableRules.length} {tableRules.length === 1 ? 'правило' : 'правил'}
            </div>
          </div>
          <span class="inline-flex text-[var(--color-text-muted-2)] transition-transform {isCollapsed ? '' : 'rotate-180'}"><Icon name="chevronDown" size={16} /></span>
        </button>

        {#if !isCollapsed}
          <div class="border-t border-[var(--color-border-3)] px-5 py-4 bg-[var(--color-bg-2)]/30">
            {#if tableRules.length === 0}
              <div class="rounded-md border border-dashed border-[var(--color-border-3)] bg-white p-5 text-center text-sm text-[var(--color-text-muted)]">
                Жодних правил. Натисніть "Додати правило" щоб створити перше.
              </div>
            {:else}
              <div class="text-[12px] text-[var(--color-text-muted)] mb-3">
                Записи вважаються дублікатами, якщо у них однакові:
              </div>
              <div class="flex flex-col items-stretch gap-0">
                {#each tableRules as r, ri (r.id)}
                  <div class="flex items-center gap-3 rounded-md border border-[var(--color-border-3)] bg-white px-4 py-2.5">
                    <span class="inline-flex text-[var(--color-text-muted-2)]"><Icon name="search" size={14} /></span>
                    <div class="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                      {#each r.fieldKeys as fk, i (fk)}
                        {#if i > 0}
                          <span class="rounded bg-[var(--color-bg-3)] px-1.5 py-0.5 text-[9.5px] font-bold text-[var(--color-text-muted-2)]">І</span>
                        {/if}
                        <span class="inline-flex items-center gap-1 rounded bg-[var(--color-bg-3)] px-2 py-0.5 text-[11.5px] font-medium">
                          <FieldTypeIcon type={fieldTypeKey(r.tableSlug, fk)} size={12} />
                          {fieldLabel(r.tableSlug, fk)}
                        </span>
                      {/each}
                    </div>
                    {#if r.onCreateBehavior === 'block'}
                      <span class="rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider">блокує</span>
                    {:else}
                      <span class="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider">попереджає</span>
                    {/if}
                    {#if r.builtIn}
                      <span class="rounded-full bg-[var(--color-pu-light)] text-[var(--color-pu-3)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider">Built-in</span>
                    {/if}
                    <button
                      type="button"
                      onclick={() => openEdit(r)}
                      class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:underline"
                    >
                      {r.builtIn ? 'переглянути' : 'редагувати'}
                    </button>
                    {#if !r.builtIn}
                      <button
                        type="button"
                        onclick={() => onDeleteRule(r)}
                        class="inline-flex items-center text-[var(--color-text-muted-2)] hover:text-red-600"
                        title="Видалити"
                      ><Icon name="delete" size={15} /></button>
                    {/if}
                  </div>
                  {#if ri < tableRules.length - 1}
                    <div class="flex justify-center py-1">
                      <span class="rounded bg-[var(--color-bg-3)] px-2 py-0.5 text-[9.5px] font-bold text-[var(--color-text-muted-2)]">АБО</span>
                    </div>
                  {/if}
                {/each}
              </div>
            {/if}

            <button
              type="button"
              onclick={() => openCreate(t.slug)}
              class="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:underline"
            >
              + Додати правило (АБО)
            </button>
          </div>
        {/if}
      </section>
    {/each}
  </div>
{/if}

<!-- Editor modal -->
{#if editor}
  {@const slug = currentSlug()}
  {@const tableName = tables.find((t) => t.slug === slug)?.name ?? slug}
  <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={closeEditor}>
    <div
      class="w-full max-w-lg rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl"
      onclick={(e) => e.stopPropagation()}
    >
      <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
        <h3 class="text-base font-bold">
          {editor.kind === 'create' ? 'Нове правило визначення дублікатів' : 'Змінити правило визначення дублікатів'}
        </h3>
        <button onclick={closeEditor} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><Icon name="close" size={16} /></button>
      </div>

      <div class="px-5 py-4 space-y-4">
        {#if isBuiltInRule}
          <div class="rounded-md border border-[var(--color-pu-light)] bg-[var(--color-pu-light)]/30 px-3 py-2 text-[11.5px] text-[var(--color-pu-3)]">
            Built-in правило — read-only. Воно вже enforced на рівні БД
            (UNIQUE-індекс або identity-resolver).
          </div>
        {/if}

        <div class="text-[11.5px] text-[var(--color-text-muted)]">
          Таблиця: <strong class="text-[var(--color-text)]">{tableName}</strong>
        </div>

        <div>
          <div class="text-sm font-semibold">1. Оберіть унікальні поля</div>
          <p class="mt-0.5 mb-2.5 text-[11px] text-[var(--color-text-muted)]">
            Записи вважаються дублікатами, якщо у них однакові значення у ВСІХ обраних полях.
          </p>

          <div class="rounded-md border border-[var(--color-border-3)] bg-[var(--color-bg-2)]/40 p-3 space-y-2">
            {#each editorFieldKeys as fk, idx (idx)}
              <div class="flex items-center gap-2">
                <span class="w-12 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
                  {idx === 0 ? 'Такий ↓' : 'І'}
                </span>
                <div class="flex-1 relative">
                  <button
                    type="button"
                    onclick={() => { pickerOpenIdx = pickerOpenIdx === idx ? null : idx; pickerSearch = ''; }}
                    disabled={isBuiltInRule}
                    class="w-full flex items-center justify-between rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-left text-sm disabled:bg-[var(--color-bg-3)] disabled:cursor-not-allowed"
                  >
                    {#if fk}
                      <span class="inline-flex items-center gap-1.5">
                        <FieldTypeIcon type={fieldTypeKey(slug, fk)} size={13} />
                        <span>{fieldLabel(slug, fk)}</span>
                      </span>
                    {:else}
                      <span class="text-[var(--color-text-muted-2)]">Оберіть поле</span>
                    {/if}
                    <span class="inline-flex text-[var(--color-text-muted-2)]"><Icon name="chevronDown" size={13} /></span>
                  </button>
                  {#if pickerOpenIdx === idx && !isBuiltInRule}
                    <div class="absolute left-0 right-0 top-full mt-1 z-10 max-h-[260px] overflow-y-auto rounded-md border border-[var(--color-border-3)] bg-white shadow-xl">
                      <div class="border-b border-[var(--color-border-3)] p-2">
                        <input
                          type="text"
                          bind:value={pickerSearch}
                          placeholder="Пошук поля…"
                          class="w-full rounded border border-[var(--color-border-3)] px-2 py-1 text-xs"
                        />
                      </div>
                      {#each filteredFields(slug, pickerSearch, editorFieldKeys.filter((_, i) => i !== idx)) as f (f.id)}
                        {@const ft = fieldType(f.type)}
                        <button
                          type="button"
                          onclick={() => pickFieldFor(idx, f.fieldKey)}
                          class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[var(--color-bg-3)]"
                        >
                          <span class="text-[12px] w-4 text-center"><FieldTypeIcon type={ft.key} size={14} /></span>
                          <span>{f.label}</span>
                          <span class="ml-auto text-[10px] text-[var(--color-text-muted-2)]">{ft.label}</span>
                        </button>
                      {:else}
                        <div class="px-3 py-3 text-center text-[11px] text-[var(--color-text-muted-2)]">Нічого не знайдено</div>
                      {/each}
                    </div>
                  {/if}
                </div>
                {#if !isBuiltInRule}
                  <button
                    type="button"
                    onclick={() => removeFieldSlot(idx)}
                    class="inline-flex items-center text-[var(--color-text-muted-2)] hover:text-red-600"
                    title="Прибрати"
                  ><Icon name="close" size={15} /></button>
                {/if}
              </div>
            {/each}

            {#if !isBuiltInRule}
              <button
                type="button"
                onclick={addFieldSlot}
                class="text-[11px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:underline"
              >
                + Додати унікальне поле (І)
              </button>
            {/if}
          </div>
        </div>

        <div>
          <div class="text-sm font-semibold">2. При створенні записів вручну</div>
          <div class="mt-2 flex flex-col gap-1.5">
            <label class="flex items-center gap-2.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 cursor-pointer">
              <input
                type="radio"
                checked={editorBehavior === 'warn'}
                disabled={isBuiltInRule}
                onchange={() => (editorBehavior = 'warn')}
              />
              <span class="text-[12.5px]">Попереджати про потенційні дублікати</span>
            </label>
            <label class="flex items-center gap-2.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 cursor-pointer">
              <input
                type="radio"
                checked={editorBehavior === 'block'}
                disabled={isBuiltInRule}
                onchange={() => (editorBehavior = 'block')}
              />
              <span class="text-[12.5px]">Забороняти створення потенційних дублікатів</span>
            </label>
          </div>
        </div>

        {#if editorErr}
          <div class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">{editorErr}</div>
        {/if}
      </div>

      <div class="flex justify-end gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
        <button
          type="button"
          onclick={closeEditor}
          class="rounded-md border border-[var(--color-border-3)] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
        >
          {isBuiltInRule ? 'Закрити' : 'Скасувати'}
        </button>
        {#if !isBuiltInRule}
          <button
            type="button"
            onclick={saveEditor}
            disabled={editorBusy}
            class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            {editorBusy ? 'Збереження…' : 'Зберегти'}
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}
